import { createSlice, Dispatch, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { LinkedInVoiceMemoMetadata } from '../../../../interfaces/db';
import { getServicesManager } from '../../../../services';
import { AppDispatch } from '../../../../store';

export interface VoiceNote extends LinkedInVoiceMemoMetadata {
  blob?: Blob;
  url?: string;
  isPlaying?: boolean;
  isLoading?: boolean;
  error?: string;
}

interface LinkedInSavedVoiceMemosState {
  vms: VoiceNote[] | null;
  loading: boolean;
  currentlyPlaying: string | null;
  loadingStates: { [key: string]: boolean };
  errors: { [key: string]: string | null };
}

const initialState: LinkedInSavedVoiceMemosState = {
  vms: null,
  loading: false,
  currentlyPlaying: null,
  loadingStates: {},
  errors: {}
};

// Async thunks
export const loadVoiceMemos = createAsyncThunk(
  'linkedin_voice_notes/loadVoiceMemos',
  async (_, { dispatch }) => {
    try {
      const voiceMemos = await getServicesManager().getLinkedInVoiceMemos();
      
      // Get current user
      const currentUser = await getServicesManager().getUser();
      
      if (voiceMemos && currentUser) {
        // Filter voice memos to only include those belonging to the current user
        const filteredMemos = voiceMemos.filter(memo => memo.user_id === currentUser.user_id);
        
        return filteredMemos.map(memo => ({
          ...memo,
          isPlaying: false,
          isLoading: false
        }));
      }
      return [];
    } catch (error) {
      throw error instanceof Error ? error.message : 'Failed to load voice memos';
    }
  }
);

export const loadVoiceMemoBlob = createAsyncThunk(
  'linkedin_voice_notes/loadVoiceMemoBlob',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      const blob = await getServicesManager().downloadLinkedInVoiceMemo(id);
      if (!blob) throw new Error('Failed to download voice memo');
      return { id, blob };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load audio');
    }
  }
);

export const createVoiceMemo = createAsyncThunk(
  'linkedin_voice_notes/createVoiceMemo',
  async ({ blob, name }: { blob: Blob, name: string }, { dispatch, rejectWithValue }) => {
    try {
      const voiceMemo = await getServicesManager().createLinkedInVoiceMemo(blob, name);
      if (!voiceMemo) throw new Error('Failed to create voice memo');
      return {
        ...voiceMemo,
        isPlaying: false,
        isLoading: false
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create voice memo');
    }
  }
);

export const updateVoiceMemoName = createAsyncThunk(
  'linkedin_voice_notes/updateVoiceMemoName',
  async ({ id, name }: { id: string, name: string }, { dispatch, rejectWithValue }) => {
    try {
      const voiceMemo = await getServicesManager().renameLinkedInVoiceMemo(id, name);
      if (!voiceMemo) throw new Error('Failed to rename voice memo');
      return voiceMemo;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to rename voice memo');
    }
  }
);

export const deleteVoiceMemoThunk = createAsyncThunk(
  'linkedin_voice_notes/deleteVoiceMemo',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      const success = await getServicesManager().deleteLinkedInVoiceMemo(id);
      if (!success) throw new Error('Failed to delete voice memo');
      return id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete voice memo');
    }
  }
);

export const linkedinVoiceNotesSlice = createSlice({
  name: 'linkedin_voice_notes',
  initialState,
  reducers: {
    addVoiceMemo: (state, action: PayloadAction<VoiceNote>) => {
      if (state.vms == null) {
        state.vms = [];
      }
      state.vms.push(action.payload);
    },
    updateVoiceMemo: (state, action: PayloadAction<VoiceNote>) => {
      if (state.vms == null) {
        state.vms = [];
      }
      const index = state.vms.findIndex(vm => vm.linkedin_voice_memo_id === action.payload.linkedin_voice_memo_id);
      if (index !== -1) {
        state.vms[index] = action.payload;
      }
    },
    deleteVoiceMemo: (state, action: PayloadAction<string>) => {
      if (state.vms == null) {
        state.vms = [];
      }
      state.vms = state.vms.filter(vm => vm.linkedin_voice_memo_id !== action.payload);
    },
    resetToDefault: (state) => {
      state.vms = [];
      state.currentlyPlaying = null;
      state.loadingStates = {};
      state.errors = {};
    },
    setVoiceMemos: (state, action: PayloadAction<VoiceNote[]>) => {
      state.vms = action.payload;
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setCurrentlyPlaying: (state, action: PayloadAction<string | null>) => {
      state.currentlyPlaying = action.payload;
    },
    setVoiceMemoBlob: (state, action: PayloadAction<{ id: string; blob: Blob }>) => {
      if (!state.vms) return;
      const memo = state.vms.find(vm => vm.linkedin_voice_memo_id === action.payload.id);
      if (memo) {
        memo.blob = action.payload.blob;
        memo.url = URL.createObjectURL(action.payload.blob);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Load voice memos
      .addCase(loadVoiceMemos.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadVoiceMemos.fulfilled, (state, action) => {
        state.vms = action.payload;
        state.loading = false;
      })
      .addCase(loadVoiceMemos.rejected, (state, action) => {
        state.loading = false;
        state.errors['load'] = action.error.message || 'Failed to load voice memos';
      })
      
      // Load voice memo blob
      .addCase(loadVoiceMemoBlob.pending, (state, action) => {
        state.loadingStates[action.meta.arg] = true;
        state.errors[action.meta.arg] = null;
      })
      .addCase(loadVoiceMemoBlob.fulfilled, (state, action) => {
        if (!state.vms) return;
        const memo = state.vms.find(vm => vm.linkedin_voice_memo_id === action.payload.id);
        if (memo) {
          memo.blob = action.payload.blob;
          memo.url = URL.createObjectURL(action.payload.blob);
        }
        state.loadingStates[action.payload.id] = false;
      })
      .addCase(loadVoiceMemoBlob.rejected, (state, action) => {
        const id = action.meta.arg;
        state.loadingStates[id] = false;
        state.errors[id] = action.error.message || 'Failed to load audio';
      })
      
      // Create voice memo
      .addCase(createVoiceMemo.pending, (state) => {
        state.loading = true;
      })
      .addCase(createVoiceMemo.fulfilled, (state, action) => {
        if (!state.vms) state.vms = [];
        state.vms.push(action.payload);
        state.loading = false;
      })
      .addCase(createVoiceMemo.rejected, (state, action) => {
        state.loading = false;
        state.errors['create'] = action.error.message || 'Failed to create voice memo';
      })
      
      // Update voice memo name
      .addCase(updateVoiceMemoName.pending, (state, action) => {
        state.loadingStates[action.meta.arg.id] = true;
      })
      .addCase(updateVoiceMemoName.fulfilled, (state, action) => {
        if (!state.vms) return;
        const index = state.vms.findIndex(vm => vm.linkedin_voice_memo_id === action.payload.linkedin_voice_memo_id);
        if (index !== -1) {
          state.vms[index] = { ...state.vms[index], ...action.payload };
        }
        state.loadingStates[action.payload.linkedin_voice_memo_id] = false;
      })
      .addCase(updateVoiceMemoName.rejected, (state, action) => {
        const id = action.meta.arg.id;
        state.loadingStates[id] = false;
        state.errors[id] = action.error.message || 'Failed to rename voice memo';
      })
      
      // Delete voice memo
      .addCase(deleteVoiceMemoThunk.pending, (state, action) => {
        state.loadingStates[action.meta.arg] = true;
      })
      .addCase(deleteVoiceMemoThunk.fulfilled, (state, action) => {
        if (!state.vms) return;
        state.vms = state.vms.filter(vm => vm.linkedin_voice_memo_id !== action.payload);
        delete state.loadingStates[action.payload];
        delete state.errors[action.payload];
      })
      .addCase(deleteVoiceMemoThunk.rejected, (state, action) => {
        const id = action.meta.arg;
        state.loadingStates[id] = false;
        state.errors[id] = action.error.message || 'Failed to delete voice memo';
      });
  }
});

export const {
  addVoiceMemo,
  updateVoiceMemo,
  deleteVoiceMemo,
  resetToDefault,
  setVoiceMemos,
  setLoading,
  setCurrentlyPlaying,
  setVoiceMemoBlob
} = linkedinVoiceNotesSlice.actions;

export function reloadLinkedInVoiceMemos(dispatch: AppDispatch) {
  dispatch(setLoading(true));
  void dispatch(loadVoiceMemos());
}

export default linkedinVoiceNotesSlice.reducer;
